import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import { url } from "../../constants/url";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import CustomTooltip from "../../components/CustomTooltip";
import AppToast from "../../components/AppToast";
import EngineerDetailsSidebar from "../../components/engineers/EngineerDetailsSidebar";
import EngineerStatusUpdateModal from "../../components/engineers/EngineerStatusUpdateModal";
import EngineerDeleteModal from "../../components/engineers/EngineerDeleteModal";
import EngineerEditModal from "../../components/engineers/EngineerEditModal";

const statusStyles = {
  Active:    { bgcolor: "#8CD47E", color: "#ffffff" },
  APPROVED:  { bgcolor: "#8CD47E", color: "#ffffff" },
  Pending:   { bgcolor: "#F8D66D", color: "#ffffff" },
  PENDING:   { bgcolor: "#F8D66D", color: "#ffffff" },
  Rejected:  { bgcolor: "#FF6961", color: "#ffffff" },
  REJECTED:  { bgcolor: "#FF6961", color: "#ffffff" },
};

const getRejectedCellStyle = (status) =>
  status === "REJECTED"
    ? {
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          borderTop: "1px solid #FF6961",
          zIndex: 1,
          pointerEvents: "none",
        },
      }
    : {};

const TechnicalEngineers = () => {
  const { token } = useAuth();
  const [engineersData, setEngineersData] = useState([]);
  const [adminData, setAdminData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEngineers, setTotalEngineers] = useState(0);
  const [pageSize] = useState(12);

  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [nameSortAnchorEl, setNameSortAnchorEl] = useState(null);
  const [emailSortAnchorEl, setEmailSortAnchorEl] = useState(null);
  const [selectedView, setSelectedView] = useState("ALL");
  const [selectedSort, setSelectedSort] = useState("LATEST");
  const [nameSort, setNameSort] = useState(null);
  const [emailSort, setEmailSort] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const showToast = (type, title, message) => setToast({ open: true, type, title, message });
  const hideToast = () => setToast((t) => ({ ...t, open: false }));

  const getDeletedEngineerIds = () => {
    try { return JSON.parse(localStorage.getItem("deletedEngineers") || "[]"); }
    catch { return []; }
  };
  const [, setDeletedEngineerIds] = useState(getDeletedEngineerIds);

  const updateEngineerStatus = async (engineerId, newStatus) => {
    try {
      setActionLoading(true);
      const eng = engineersData.find((e) => e._id === engineerId);
      const statusMap = { Active: "APPROVED", Rejected: "REJECTED", Pending: "PENDING" };
      await axios.post(`${url}/api/admin/approve-req`,
        { userID: engineerId, action: statusMap[newStatus] || newStatus.toUpperCase() },
        { headers: { Authorization: `Bearer ${token}` } });
      showToast("success", "Status Updated", `<strong>${eng?.name || "Engineer"}'s</strong> status changed to <strong>${newStatus}</strong>`);
      fetchEngineers();
    } catch (err) {
      showToast("error", "Update Failed", err.response?.data?.message || err.message);
    } finally { setActionLoading(false); }
  };

  const handleDeleteEngineer = async (engineer) => {
    if (!engineer?._id) return;
    try {
      setActionLoading(engineer._id + "delete");
      const resp = await axios.delete(`${url}/api/users/delete/${engineer._id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (resp.data.success || resp.status === 200) {
        const ids = getDeletedEngineerIds();
        if (!ids.includes(engineer._id)) {
          const updated = [...ids, engineer._id];
          localStorage.setItem("deletedEngineers", JSON.stringify(updated));
          setDeletedEngineerIds(updated);
        }
        setEngineersData((prev) => prev.filter((e) => e._id !== engineer._id));
        setTotalEngineers((prev) => Math.max(0, prev - 1));
        if (engineersData.length === 1 && page > 1) setPage((p) => p - 1);
        showToast("success", "Engineer Deleted", `<strong>${engineer.name}</strong> has been permanently deleted`);
      }
    } catch (err) {
      showToast("error", "Error", err.response?.data?.message || err.message);
    } finally { setActionLoading(null); }
  };

  const handleStatusUpdate = (selectedStatus) => {
    if (selectedEngineer) { updateEngineerStatus(selectedEngineer._id, selectedStatus); setStatusModalOpen(false); setSelectedEngineer(null); }
  };
  const handleViewDetails  = (eng) => { setSelectedEngineer(eng); setIsSidebarOpen(true); };
  const handleStatusClick  = (eng) => { setSelectedEngineer(eng); setStatusModalOpen(true); };
  const handleEditClick    = (eng) => { setSelectedEngineer(eng); setEditModalOpen(true); };
  const handleDeleteClick  = (eng) => { setSelectedEngineer(eng); setDeleteOpen(true); };
  const handleConfirmDelete = (eng) => { handleDeleteEngineer(eng); setDeleteOpen(false); setSelectedEngineer(null); };

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED": return "Active";
      case "PENDING":  return "Pending";
      case "REJECTED": return "Rejected";
      default:         return status || "Pending";
    }
  };

  const getLocation = (engineer) => {
    if (!engineer) return "-";
    const parts = [];
    if (engineer.city)  parts.push(engineer.city);
    if (engineer.state) parts.push(engineer.state);
    return parts.length > 0 ? parts.join(", ") : engineer.detailedAddress || "-";
  };

  const getProjectCounts = (engineer) => {
    if (!engineer) return { completed: 0, assigned: 0, display: "0/0" };
    const completed = engineer.projectsRecords?.projectCompleted || 0;
    const assigned  = engineer.projectsRecords?.projectAssigned  || 0;
    return { completed, assigned, display: `${completed}/${assigned}` };
  };

  const isFilterApplied = () =>
    selectedView !== "ALL" || selectedSort !== "LATEST" ||
    nameSort !== null || emailSort !== null || searchQuery !== "";

  const fetchEngineers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append("role", "ENGINEER");
      params.append("page", page);
      params.append("limit", pageSize);

      if (selectedView !== "ALL") params.append("status", selectedView);
      if (searchQuery.trim()) params.append("search", searchQuery.trim());

      // sort
      if (nameSort === "AZ")  params.append("sort", "az");
      else if (nameSort === "ZA") params.append("sort", "za");
      else if (emailSort === "AZ") params.append("sort", "emailAZ");
      else if (emailSort === "ZA") params.append("sort", "emailZA");
      else {
        const sortMap = { LATEST: "latest", OLDEST: "oldest" };
        params.append("sort", sortMap[selectedSort] || "latest");
      }

      const response = await axios.get(
        `${url}/api/users/fetch?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data) {
        setEngineersData(response.data.users || []);
        setTotalEngineers(response.data.total || 0);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch engineers");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, token, selectedSort, selectedView, nameSort, emailSort, searchQuery]);

  const fetchAdmins = useCallback(async () => {
    try {
      const [adminRes, superRes] = await Promise.all([
        axios.get(`${url}/api/users/fetch?role=ADMIN&limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${url}/api/users/fetch?role=SUPERADMIN&limit=50`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const admins = [
        ...(adminRes.data?.users || []),
        ...(superRes.data?.users || [])
      ];
      setAdminData(admins);
    } catch (err) {
      console.error("Failed to fetch admins", err);
    }
  }, [token]);

  useEffect(() => { 
    fetchEngineers(); 
    fetchAdmins();
  }, [fetchEngineers, fetchAdmins]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const clearFilters = () => {
    setSelectedView("ALL");
    setSelectedSort("LATEST");
    setNameSort(null);
    setEmailSort(null);
    setSearchQuery("");
    setPage(1);
  };

  const startIndex = (page - 1) * pageSize + 1;
  const endIndex   = Math.min(page * pageSize, totalEngineers);

  if (loading && engineersData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  const tableHeaders = [
    { key: "Name",                   hasSort: true,  align: "left",   center: false },
    { key: "Email Address",          hasSort: true,  align: "left",   center: false },
    { key: "Phone",                  hasSort: false, align: "left",   center: false },
    { key: "Status",                 hasSort: false, align: "left",   center: false },
    { key: "Counts",                 hasSort: false, align: "center", center: true  },
    { key: "Location",               hasSort: false, align: "left",   center: false },
    { key: "Field Inspection Stats", hasSort: false, align: "center", center: true  },
    { key: "Actions",                hasSort: false, align: "left",   center: false },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
          Engineers
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchEngineers(); }}
            placeholder="Search..."
            size="small"
            variant="outlined"
            sx={{
              background: "#fff",
              borderRadius: 2,
              width: { xs: "100%", md: 240 },
              "& .MuiOutlinedInput-root": {
                height: "36px",
                "& fieldset": { borderColor: COLORS.LIGHT_SECONDARY },
                "&:hover fieldset": { borderColor: COLORS.SECONDARY },
                "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
              },
            }}
          />
          <CustomTooltip title="Sort">
            <IconButton onClick={(e) => setSortAnchorEl(e.currentTarget)}>
              <Icon name="sort" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip title="View">
            <IconButton onClick={(e) => setViewAnchorEl(e.currentTarget)}>
              <Icon name="list" color={COLORS.SECONDARY} />
            </IconButton>
          </CustomTooltip>
          {isFilterApplied() && (
            <CustomTooltip title="Clear Filters">
              <IconButton onClick={clearFilters}>
                <Icon name="rotate" color={COLORS.ERROR} />
              </IconButton>
            </CustomTooltip>
          )}
        </Box>
      </Box>

      {error && !loading && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper elevation={0} sx={{ backgroundColor: "transparent" }}>
        <TableContainer>
          <Table
            sx={{
              backgroundColor: "transparent",
              "& thead th": { backgroundColor: "transparent" },
              "& tbody td": {
                backgroundColor: "transparent",
                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              },
            }}
          >
            <TableHead>
              <TableRow>
                {tableHeaders.map((head) => (
                  <TableCell
                    key={head.key}
                    align={head.align}
                    sx={{
                      ...TYPOGRAPHY.H4,
                      color: COLORS.PRIMARY,
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      whiteSpace: "nowrap",
                      width:
                        head.key === "Location" ? "180px"
                        : head.key === "Field Inspection Stats" ? "150px"
                        : "auto",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: head.center ? "center" : "space-between",
                        gap: 1,
                      }}
                    >
                      <span>{head.key}</span>
                      {head.hasSort && (
                        <CustomTooltip title="Sort">
                          <Icon
                            name="sort"
                            color={COLORS.LIGHT_SECONDARY}
                            sx={{
                              fontSize: 16,
                              cursor: "pointer",
                              color:
                                head.key === "Name" && nameSort
                                  ? COLORS.ERROR
                                  : head.key === "Email Address" && emailSort
                                  ? COLORS.ERROR
                                  : COLORS.LIGHT_SECONDARY,
                            }}
                            onClick={(e) => {
                              if (head.key === "Name") setNameSortAnchorEl(e.currentTarget);
                              else if (head.key === "Email Address") setEmailSortAnchorEl(e.currentTarget);
                            }}
                          />
                        </CustomTooltip>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && engineersData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : (engineersData.length === 0 && adminData.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography sx={{ color: COLORS.TEXT }}>No engineers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (page === 1 ? [...adminData, ...engineersData] : engineersData).map((engineer) => {
                  const projectInfo    = getProjectCounts(engineer);
                  const frontendStatus = getStatusText(engineer.status);
                  const location       = getLocation(engineer);
                  const fis            = engineer.fieldInspectionStats;
                  const isAdminRow     = ["ADMIN", "SUPERADMIN"].includes(engineer.role);

                  return (
                    <TableRow
                      key={engineer._id || engineer.id}
                      sx={{
                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        "&:hover": { bgcolor: "rgba(101,45,144,0.03)" },
                        ...(isAdminRow && { bgcolor: "rgba(101,45,144,0.05)" })
                      }}
                    >
                      {/* Name */}
                      <TableCell sx={{ ...getRejectedCellStyle(engineer.status) }}>
                        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, display: "flex", alignItems: "center" }}>
                          {engineer.name || "N/A"}
                          {isAdminRow && (
                            <Chip label="Admin" size="small" sx={{ ml: 1, height: 20, fontSize: "0.65rem", bgcolor: COLORS.PRIMARY, color: "white" }} />
                          )}
                        </Typography>
                      </TableCell>

                      {/* Email */}
                      <TableCell sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(engineer.status) }}>
                        {engineer.email || "-"}
                      </TableCell>

                      {/* Phone */}
                      <TableCell sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(engineer.status) }}>
                        {engineer.phone || "-"}
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ ...getRejectedCellStyle(engineer.status) }}>
                        <Chip
                          label={frontendStatus}
                          size="small"
                          sx={{
                            ...(statusStyles[engineer.status] || statusStyles.Pending),
                            ...TYPOGRAPHY.BODY_SMALL,
                            height: 22,
                          }}
                        />
                      </TableCell>

                      {/* Counts — Y / N / Reports */}
                      <TableCell align="center" sx={{ ...getRejectedCellStyle(engineer.status) }}>
                        {fis ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "center" }}>
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                              <Chip
                                label={`Y: ${fis.yesCount ?? 0}`}
                                size="small"
                                sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                              />
                              <Chip
                                label={`N: ${fis.noCount ?? 0}`}
                                size="small"
                                sx={{ bgcolor: "#FFEBEE", color: "#C62828", height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                              />
                            </Box>
                            <Typography sx={{ fontSize: "0.75rem", color: COLORS.SECONDARY, textAlign: "center" }}>
                              Reports: <strong>{fis.totalReports ?? 0}</strong>
                            </Typography>
                            {(fis.criticalCount ?? 0) > 0 && (
                              <Typography sx={{ fontSize: "0.75rem", color: COLORS.ERROR, fontWeight: 700, textAlign: "center" }}>
                                Critical: {fis.criticalCount}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: "0.8rem" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Location */}
                      <TableCell sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(engineer.status), width: "180px" }}>
                        {location}
                      </TableCell>

                      {/* Field Inspection Stats — Projects completed/assigned */}
                      <TableCell align="center" sx={{ color: COLORS.TEXT, ...TYPOGRAPHY.BODY_LARGE, ...getRejectedCellStyle(engineer.status), width: "150px" }}>
                        <CustomTooltip
                          title={
                            <Box>
                              <Typography variant="caption" display="block">Completed: {projectInfo.completed}</Typography>
                              <Typography variant="caption" display="block">Assigned: {projectInfo.assigned}</Typography>
                            </Box>
                          }
                        >
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            {projectInfo.display}
                          </Box>
                        </CustomTooltip>
                      </TableCell>

                      {/* Actions */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <CustomTooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(engineer)}>
                              <Icon name="Action1" color={COLORS.TEXT} />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Change Status">
                            <IconButton size="small" onClick={() => handleStatusClick(engineer)} disabled={!!actionLoading}>
                              <Icon name="Action2" color={COLORS.TEXT} />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Edit Details">
                            <IconButton size="small" onClick={() => handleEditClick(engineer)}>
                              <Icon name="Action3" color={COLORS.PRIMARY} />
                            </IconButton>
                          </CustomTooltip>
                          <CustomTooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeleteClick(engineer)}>
                              <Icon name="Action4" color={COLORS.ERROR} />
                            </IconButton>
                          </CustomTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && engineersData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, p: 2 }}>
            <Typography sx={{ fontSize: 13, color: "#777" }}>
              {startIndex} - {endIndex} of {totalEngineers}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                <Icon name="chevron-left" />
              </IconButton>
              {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                let pageNum;
                if (totalPages <= 3)        pageNum = i + 1;
                else if (page === 1)         pageNum = i + 1;
                else if (page === totalPages) pageNum = totalPages - 2 + i;
                else                         pageNum = page - 1 + i;
                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      sx={{
                        minWidth: 32, height: 32,
                        bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                        color:   page === pageNum ? "#fff" : COLORS.TEXT,
                        border:  page === pageNum ? "none" : "1px solid #E1BEE7",
                        "&:hover": { bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)" },
                      }}
                    >
                      {pageNum}
                    </Button>
                  );
                }
                return null;
              })}
              <IconButton onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                <Icon name="chevron-right" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Sort Menu */}
      <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={() => setSortAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0px 6px 18px rgba(0,0,0,0.15)", px: 0.5 } }}>
        {[{ label: "Latest First", value: "LATEST" }, { label: "Oldest First", value: "OLDEST" }].map((item) => (
          <MenuItem key={item.value} onClick={() => { setSelectedSort(item.value); setSortAnchorEl(null); setPage(1); }}
            sx={{ gap: 1.5, borderRadius: 1, color: selectedSort === item.value ? COLORS.PRIMARY : COLORS.TEXT }}>
            <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* View Menu */}
      <Menu anchorEl={viewAnchorEl} open={Boolean(viewAnchorEl)} onClose={() => setViewAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0px 6px 18px rgba(0,0,0,0.15)", px: 0.5 } }}>
        {[
          { label: "All",      value: "ALL" },
          { label: "Active",   value: "APPROVED" },
          { label: "Pending",  value: "PENDING" },
          { label: "Rejected", value: "REJECTED" },
        ].map((item) => (
          <MenuItem key={item.value} onClick={() => { setSelectedView(item.value); setViewAnchorEl(null); setPage(1); }}
            sx={{ gap: 1.5, borderRadius: 1, color: selectedView === item.value ? COLORS.PRIMARY : COLORS.TEXT }}>
            <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Name Sort Menu */}
      <Menu anchorEl={nameSortAnchorEl} open={Boolean(nameSortAnchorEl)} onClose={() => setNameSortAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0px 6px 18px rgba(0,0,0,0.15)", px: 0.5 } }}>
        {[{ label: "A to Z", value: "AZ", icon: "foldUp" }, { label: "Z to A", value: "ZA", icon: "foldDown" }].map((item) => {
          const isActive = nameSort === item.value;
          return (
            <MenuItem key={item.value} onClick={() => { setNameSort(item.value); setEmailSort(null); setNameSortAnchorEl(null); setPage(1); }}
              sx={{ gap: 1.5, borderRadius: 1, color: isActive ? COLORS.PRIMARY : COLORS.TEXT }}>
              <Icon name={item.icon} size={18} color={isActive ? COLORS.PRIMARY : COLORS.TEXT} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Email Sort Menu */}
      <Menu anchorEl={emailSortAnchorEl} open={Boolean(emailSortAnchorEl)} onClose={() => setEmailSortAnchorEl(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: "0px 6px 18px rgba(0,0,0,0.15)", px: 0.5 } }}>
        {[{ label: "A to Z", value: "AZ", icon: "foldUp" }, { label: "Z to A", value: "ZA", icon: "foldDown" }].map((item) => {
          const isActive = emailSort === item.value;
          return (
            <MenuItem key={item.value} onClick={() => { setEmailSort(item.value); setNameSort(null); setEmailSortAnchorEl(null); setPage(1); }}
              sx={{ gap: 1.5, borderRadius: 1, color: isActive ? COLORS.PRIMARY : COLORS.TEXT }}>
              <Icon name={item.icon} size={18} color={isActive ? COLORS.PRIMARY : COLORS.TEXT} />
              <Typography sx={TYPOGRAPHY.BODY_LARGE}>{item.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Details Sidebar */}
      {isSidebarOpen && selectedEngineer && (
        <EngineerDetailsSidebar
          open={isSidebarOpen}
          engineer={selectedEngineer}
          onClose={() => { setIsSidebarOpen(false); setSelectedEngineer(null); }}
        />
      )}

      {/* Status Modal */}
      {statusModalOpen && selectedEngineer && (
        <EngineerStatusUpdateModal
          open={statusModalOpen}
          engineer={selectedEngineer}
          onClose={() => { setStatusModalOpen(false); setSelectedEngineer(null); }}
          onStatusUpdate={handleStatusUpdate}
        />
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedEngineer && (
        <EngineerEditModal
          open={editModalOpen}
          engineer={selectedEngineer}
          onClose={() => { setEditModalOpen(false); setSelectedEngineer(null); }}
          onSave={() => { setEditModalOpen(false); setSelectedEngineer(null); fetchEngineers(); }}
        />
      )}

      {/* Delete Modal */}
      {deleteOpen && selectedEngineer && (
        <EngineerDeleteModal
          open={deleteOpen}
          engineer={selectedEngineer}
          onClose={() => { setDeleteOpen(false); setSelectedEngineer(null); }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {/* Toast */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
      />
    </Box>
  );
};

export default TechnicalEngineers;
